import { useState, type ReactNode } from 'react';
import { Layout } from './Layout.js';
import { apiPost, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';

/**
 * Poort voor Mila en Sara (besluit Jos 3 sep 2026): zonder `aiTools` op het
 * account blijft het menu-item staan, maar de pagina legt uit wat het is en
 * biedt "Toegang aanvragen". De backend weigert hoe dan ook (403); dit is
 * alleen de nette voorkant. Demo-accounts hebben geen vinkje en gaan door.
 */
export function AiToolsGate({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  const [verstuurd, setVerstuurd] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  if (!user || user.aiTools === true || user.demo === true) return <>{children}</>;

  async function vraagAan(): Promise<void> {
    setBezig(true);
    setFout(null);
    try {
      await apiPost('/api/v1/users/ai-tools-aanvraag');
      setVerstuurd(true);
    } catch (err) {
      setFout(err instanceof ApiError ? err.message : 'Aanvragen is mislukt. Probeer het opnieuw.');
    } finally {
      setBezig(false);
    }
  }

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-5">
        <h1 className="text-xl font-bold text-ink-900">Mila en Sara</h1>
        <div className="mt-3 rounded-2xl border border-brand-100 bg-white p-5 text-[14px] text-ink-900/80">
          {/* Zelfde formulering als het welkomstpaneel (besluit Jos 4 sep 2026):
              de teamleider is de route, de knop is het mechanisme dat kantoor
              de aanvraag laat zien in Beheer Gebruikers. */}
          <p>
            De Kennishub heeft twee AI-hulpmiddelen. <strong>Mila</strong> beantwoordt
            vakinhoudelijke vragen op basis van een gecontroleerde kennisverzameling en vermeldt
            daarbij haar bronnen. <strong>Sara</strong> ondersteunt bij het uitwerken van
            gesprekken.
          </p>
          <p className="mt-3">
            Deze twee worden stapsgewijs uitgerold en staan voor jouw account nog niet aan. Wil je
            ermee werken? Vraag toegang aan via je teamleider. Met de knop hieronder meld je je
            aanvraag alvast bij kantoor; je ziet de tools bij de volgende keer inloggen.
          </p>
          {verstuurd ? (
            <p className="mt-4 rounded-lg bg-brand-50 px-4 py-3 text-brand-700">
              Aanvraag verstuurd. Kantoor neemt hem in behandeling.
            </p>
          ) : (
            <button
              type="button"
              onClick={() => void vraagAan()}
              disabled={bezig}
              className="mt-4 rounded-lg bg-brand-600 px-4 py-2 text-[14px] font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {bezig ? 'Bezig…' : 'Toegang aanvragen'}
            </button>
          )}
          {fout && <p className="mt-3 rounded-lg bg-red-50 px-4 py-3 text-red-700">{fout}</p>}
        </div>
      </div>
    </Layout>
  );
}
